import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { auth, db } from '../../lib/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import './AdminLoginPage.css';

export default function AdminLoginPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/admin';

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Sign in with Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if user is an admin by checking Firestore
      const adminDoc = doc(db, 'admin_users', user.uid);
      const adminSnapshot = await getDoc(adminDoc);
      
      if (!adminSnapshot.exists()) {
        setError('관리자 권한이 없습니다.');
        // Sign out the user since they're not an admin
        await auth.signOut();
        return;
      }
      
      
      // Update last login timestamp
      try {
        await updateDoc(adminDoc, {
          last_login_at: new Date().toISOString()
        });
      } catch (updateError) {
        console.error('Error updating last login:', updateError);
        // Don't fail the login just because we couldn't update the timestamp
      }
        
      // Redirect to admin dashboard
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found') {
        setError('등록되지 않은 사용자입니다.');
      } else if (err.code === 'auth/wrong-password') {
        setError('비밀번호가 올바르지 않습니다.');
      } else if (err.code === 'auth/invalid-email') {
        setError('잘못된 이메일 형식입니다.');
      } else {
        setError('로그인 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-wrapper">
        {/* 로고와 타이틀 */}
        <div className="admin-login-header">
          <h1 className="admin-login-title">관리자 로그인</h1>
          <p className="admin-login-subtitle">광천건설 인테리어 관리 시스템</p>
        </div>

        {/* 로그인 폼 */}
        <div className="admin-login-form-container">
          <form onSubmit={handleLogin} className="admin-login-form">
            {/* 에러 메시지 */}
            {error && (
              <div className="admin-error-container">
                <div className="admin-error-content">
                  <div className="admin-error-icon">
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="admin-error-text">{error}</div>
                </div>
              </div>
            )}
            
            {/* 이메일 입력 */}
            <div className="admin-form-group">
              <label htmlFor="email" className="admin-form-label">
                이메일
              </label>
              <div className="admin-form-input-wrapper">
                <Mail className="admin-form-icon" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="admin-form-input"
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            {/* 비밀번호 입력 */}
            <div className="admin-form-group">
              <label htmlFor="password" className="admin-form-label">
                비밀번호
              </label>
              <div className="admin-form-input-wrapper">
                <Lock className="admin-form-icon" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="admin-form-input"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="admin-password-toggle"
                >
                  <span className="admin-password-toggle-button">
                    {showPassword ? (
                      <EyeOff className="admin-password-toggle-icon" />
                    ) : (
                      <Eye className="admin-password-toggle-icon" />
                    )}
                  </span>
                </button>
              </div>
            </div>

            {/* 로그인 버튼 */}
            <button
              type="submit"
              disabled={loading}
              className="admin-login-button"
            >
              {loading ? '로그인 중...' : '로그인'}
            </button>
          </form>
        </div>

        {/* 하단 정보 */}
        <div className="admin-footer">
          
        </div>
      </div>
    </div>
  );
}
